/**
 * Google Charts Loader Utility
 * Ensures that the Google Charts library is loaded and initialized before use.
 */

let _googleChartsPromise: Promise<any> | null = null;

export function ensureGoogleCharts(): Promise<any> {
    if (_googleChartsPromise) return _googleChartsPromise;

    _googleChartsPromise = new Promise((resolve, reject) => {
        const loadCharts = () => {
            const google = (window as any).google;
            if (!google || !google.charts) {
                reject(new Error("Google Charts library not found after script load."));
                return;
            }
            google.charts.load('current', { packages: ['corechart'] });
            google.charts.setOnLoadCallback(() => resolve(google));
        };

        if ((window as any).google && (window as any).google.charts) {
            loadCharts();
        } else {
            // Check if script is already being loaded in the document
            const scripts = document.getElementsByTagName('script');
            let foundScript: HTMLScriptElement | null = null;
            for (let i = 0; i < scripts.length; i++) {
                if (scripts[i].src === 'https://www.gstatic.com/charts/loader.js') {
                    foundScript = scripts[i];
                    break;
                }
            }

            if (foundScript) {
                if ((window as any).google) { // Script might have loaded but not initialized charts yet
                   loadCharts();
                } else {
                    foundScript.addEventListener('load', loadCharts);
                    foundScript.addEventListener('error', () => {
                        _googleChartsPromise = null; // Allow retry
                        reject(new Error("Failed to load Google Charts script."));
                    });
                }
            } else {
                // Inject the script if not found
                const script = document.createElement('script');
                script.src = 'https://www.gstatic.com/charts/loader.js';
                script.type = 'text/javascript';
                script.async = true;
                script.onload = loadCharts;
                script.onerror = () => {
                    _googleChartsPromise = null; // Allow retry
                    reject(new Error("Failed to load Google Charts script via injection."));
                };
                document.head.appendChild(script);
            }
        }
    });

    return _googleChartsPromise;
}
